import {
  init,
  MapCanvasApi,
  initializeClusters,
  updateRenderedElements,
  Size,
  SpotElement,
  DecorationElement,
} from '@spiaggeit/map-canvas'
import { Color, isSpotSubType } from '@spiaggeit/spit-core'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebounceCallback } from 'usehooks-ts'
import { useAppDispatch, useAppSelector } from '@/hooks/store'
import { usePrevious } from '@/hooks/usePrevious'
import { BoundingBox2d, MapElementWithSectorId } from '@/models/map'
import { ServiceInfoPurchaseType } from '@/models/service'
import { cartSlice } from '@/store/cartSlice'
import {
  loadMapData,
  loadSectors,
  mapSelectors,
  mapSlice,
} from '@/store/mapSlice'
import { servicesSlice } from '@/store/servicesSlice'
import { toastSlice } from '@/store/toastSlice'
import {
  MAP_CELL_SIZE,
  USER_HAS_VISITED_MAP_BEFORE_KEY,
} from '@/utils/constants'

function calculateInitialZoom(
  containerRect: { width: number; height: number },
  mapQuadrant: BoundingBox2d
) {
  const quadrantWidth = mapQuadrant.max.x - mapQuadrant.min.x
  const quadrantHeight = mapQuadrant.max.y - mapQuadrant.min.y
  const scaleX = containerRect.width / quadrantWidth
  const scaleY = containerRect.height / quadrantHeight
  return Math.min(scaleX, scaleY)
}
const CLUSTER_SIZE: Size = {
  height: 300,
  width: 300,
}

export const useMapUtils = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [isCanvasReady, setIsCanvasReady] = useState(false)
  const canvasAPIRef = useRef<MapCanvasApi | null>(null)
  const mapData = useAppSelector(mapSelectors.mapData)
  const cartItemsIds = useAppSelector(cartSlice.selectors.itemsIds)
  const previousCartItemsIds = usePrevious(cartItemsIds)

  const handleMapElementClick = (elementId: string) => {
    if (!mapData?.elements) return

    const element = mapData.elements.find((element) => element.id == elementId)

    if (!element || !element.style) return
    const isAvailable = element.style.iconColor !== Color.GREY

    if (isAvailable) {
      dispatch(cartSlice.actions.setCartPopoverIsOpen(false))
      dispatch(mapSlice.actions.selectMapElement(element))
    } else {
      dispatch(
        toastSlice.actions.show({
          ctaCopy: t('common.messageUnderstood'),
          message: t('map.errors.unavailableMapSpot'),
        })
      )
    }
  }

  const resetCanvasHeightHandler = useDebounceCallback(() => {
    const firstChild = document.getElementById('canvas')
      ?.firstChild as HTMLDivElement

    if (!firstChild) return

    firstChild.style.height = ''
  }, 100)

  const [availableSpots, setAvailableSpots] = useState<
    MapElementWithSectorId[]
  >([])

  const userHasVisitedMapBefore = localStorage.getItem(
    USER_HAS_VISITED_MAP_BEFORE_KEY
  )

  return {
    availableSpots: {
      areMoreThanOne: availableSpots && availableSpots.length > 1,
      areNone: availableSpots && availableSpots.length < 1,
      array: availableSpots,
      isOnlyOne: availableSpots && availableSpots.length === 1,
    },
    getCanvasAPI: () => {
      if (!canvasAPIRef.current) {
        throw new Error('Map canvas API is not available.')
      }

      return canvasAPIRef.current
    },
    highlightSelectedItems: function () {
      try {
        if (!mapData?.elements) {
          throw new Error('Map elements are not available.')
        }
        const api = this.getCanvasAPI()

        const newElements = cartItemsIds.filter(
          (element) => !previousCartItemsIds?.includes(element)
        )
        const removedElements =
          previousCartItemsIds?.filter(
            (element) => !cartItemsIds.includes(element)
          ) ?? []
        // when they match, we took the last value because it should match the initial render
        if (newElements.length === 0 && removedElements.length === 0) {
          cartItemsIds.forEach((id) => {
            api.highlightElement(id, Color.BLUE)
          })
        } else {
          api.clearHighlight(removedElements)
          newElements.forEach((id) => {
            api.highlightElement(id, Color.BLUE)
          })
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
      }
    },
    initMap: async function () {
      if (
        !mapData ||
        !mapData.elements ||
        !mapData.background ||
        !mapData.size
      ) {
        throw new Error('Required map data is not available.')
      }

      const mapSize = {
        height: mapData.size.height * MAP_CELL_SIZE,
        width: mapData.size.width * MAP_CELL_SIZE,
      }

      // Default to map center if for some reason we can't find the best quadrant
      // Happens when there are no available spots
      const initialPosition = {
        x: mapSize.width / 2,
        y: mapSize.height / 2,
      }

      const elements = mapData.elements ?? []
      let initialZoom = undefined
      const container = document.getElementById('canvas')!
      const boundingClientRect = container.getBoundingClientRect()
      // If we identified the best quadrant, we override the initial map center position
      if (mapData.bestQuadrant != null) {
        initialPosition.x = mapData.bestQuadrant.center.x
        initialPosition.y = mapData.bestQuadrant.center.y
        initialZoom = calculateInitialZoom(
          boundingClientRect,
          mapData.bestQuadrant
        )
      }

      canvasAPIRef.current = await init({
        events: {
          onMapElementClick: handleMapElementClick,
          onMapPanZoom() {
            if (canvasAPIRef.current !== null) {
              updateRenderedElements(canvasAPIRef.current, clusters)
            }
          },
        },
        opts: {
          assetsBaseUrl: `${import.meta.env.VITE_ASSETS_URL}/booking/map`,
          background: mapData.background,
          containerId: 'canvas',
          elements: [],
          initialPosition: {
            x: -initialPosition.x,
            y: -initialPosition.y,
          },
          initialZoom: initialZoom,
          size: mapSize,
        },
      })

      if (mapData.bestQuadrant != null) {
        // Center canvas on position
        const stageInfo = canvasAPIRef.current.getStageInfo()
        initialPosition.x -= stageInfo.rect.width / 2
        initialPosition.y -= stageInfo.rect.height / 2
        canvasAPIRef.current.goToPosition({
          x: -initialPosition.x,
          y: -initialPosition.y,
        })
      }

      const groupedElements = elements.reduce(
        (acc, item) => {
          if (isSpotSubType(item.subType)) {
            acc.spots.push(item as SpotElement)
            if (item.style?.iconColor === Color.GREEN) {
              setAvailableSpots((state) => state.concat([item]))
            }
          } else {
            acc.decorations.push(item as DecorationElement)
          }
          return acc
        },
        { decorations: [], spots: [] } as {
          decorations: DecorationElement[]
          spots: SpotElement[]
        }
      )

      canvasAPIRef.current.addElements(groupedElements.decorations)

      const clusters = initializeClusters(
        {
          height: mapSize.height,
          width: mapSize.width,
        },
        CLUSTER_SIZE,
        groupedElements.spots
      )
      await updateRenderedElements(canvasAPIRef.current, clusters)

      setIsCanvasReady(true)

      this.highlightSelectedItems()
    },
    isCanvasReady,
    loadInitialData: async function () {
      try {
        await dispatch(
          loadMapData({
            cellSize: MAP_CELL_SIZE,
            screenHeight: window?.innerHeight ?? 0,
            screenWidth: window?.innerWidth ?? 0,
          })
        ).unwrap()
        dispatch(loadSectors())
        dispatch(
          servicesSlice.actions.load({
            purchaseType: ServiceInfoPurchaseType.WITH_SPOT,
          })
        )
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load data:', error)
      }
    },
    resetCanvasHeightHandler,
    userHasVisitedMapBefore,
    zoomIn: function () {
      canvasAPIRef.current?.zoomIn()
    },
    zoomOut: function () {
      canvasAPIRef.current?.zoomOut()
    },
  }
}
